// Libs
import { Box, Link } from '@mui/material';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseAvatarGroup, BaseTable, BaseTextEditor } from '~/components/common';
// Others
import { IHeaderCellType } from '~/utils/interface/common';
import { IActivity, IActivityDetail } from '~/utils/interface/activity';
import { EMPTY_STRING } from '~/utils/constants/common';
import {
  ActivityNameEnum,
  BusinessModelEnum,
  DateFormatEnum,
  ModeTextEditorEnum,
  ThemeTextEditorEnum,
} from '~/utils/enum/common';
import { convertToTitleCase, formatDate, formatTitleCase } from '~/utils/helper/common';
import { varColor } from '~/themes/themeVariables';

type Props = {
  data: IActivity;
};

const columnsDefault = (t: TFunction): IHeaderCellType<IActivityDetail>[] => {
  return [
    {
      id: 'name',
      label: t('activity_detail_table_title_name'),
      render: (record: IActivityDetail) => convertToTitleCase(record?.name),
    },
    {
      id: 'oldValue',
      label: t('activity_detail_table_title_old_value'),
      minWidth: 100,
      render: (record: IActivityDetail) =>
        record?.oldValue != null ? String(record?.oldValue) : EMPTY_STRING,
    },
    {
      id: 'newValue',
      label: t('activity_detail_table_title_new_value'),
      render: (record: IActivityDetail) =>
        record?.newValue != null ? String(record?.newValue) : EMPTY_STRING,
    },
  ];
};

const columnsNote = (t: TFunction): IHeaderCellType<IActivityDetail>[] => {
  return [
    {
      id: 'name',
      label: t('activity_detail_table_title_name'),
      render: (record: IActivityDetail) => convertToTitleCase(record?.name),
    },
    {
      id: 'oldValue',
      label: t('activity_detail_table_title_old_value'),
      minWidth: 100,
      render: (record: IActivityDetail) => (
        <BaseTextEditor
          mode={ModeTextEditorEnum.VIEW}
          theme={ThemeTextEditorEnum.BUBBLE}
          value={record.oldValue}
          width={'100%'}
        />
      ),
    },
    {
      id: 'newValue',
      label: t('activity_detail_table_title_new_value'),
      render: (record: IActivityDetail) => (
        <BaseTextEditor
          mode={ModeTextEditorEnum.VIEW}
          theme={ThemeTextEditorEnum.BUBBLE}
          value={record.newValue}
          width={'100%'}
        />
      ),
    },
  ];
};

const columnsJob = (t: TFunction): IHeaderCellType<IActivityDetail>[] => {
  const renderValue = (name: string, value: any) => {
    switch (name) {
      case ActivityNameEnum.ASSIGNEE:
        return <BaseAvatarGroup avatars={value || []} borderColor={varColor.white} max={5} />;

      case ActivityNameEnum.REFERRAL_TYPE:
      case ActivityNameEnum.SOURCE:
        return formatTitleCase(value || EMPTY_STRING);

      case ActivityNameEnum.START_DATE:
      case ActivityNameEnum.END_DATE:
        return formatDate(value, DateFormatEnum.DD_MMM_YYYY);

      case ActivityNameEnum.TOTAL:
        return value != null ? `$${value}` : EMPTY_STRING;

      case ActivityNameEnum.CALLBACK:
        return value != null ? formatTitleCase(String(value)) : EMPTY_STRING;

      default:
        return value != null ? String(value) : EMPTY_STRING;
    }
  };

  return [
    {
      id: 'name',
      label: t('activity_detail_table_title_name'),
      render: (record: IActivityDetail) => convertToTitleCase(record?.name),
    },
    {
      id: 'oldValue',
      label: t('activity_detail_table_title_old_value'),
      minWidth: 100,
      render: (record: IActivityDetail) => renderValue(record?.name, record?.oldValue),
    },
    {
      id: 'newValue',
      label: t('activity_detail_table_title_new_value'),
      render: (record: IActivityDetail) => renderValue(record?.name, record?.newValue),
    },
  ];
};

const columnsSchedule = (t: TFunction): IHeaderCellType<IActivityDetail>[] => {
  const renderValue = (name: string, value: any) => {
    switch (name) {
      case ActivityNameEnum.ASSIGNEE:
        return <BaseAvatarGroup avatars={value || []} borderColor={varColor.white} max={5} />;

      case ActivityNameEnum.START_DATE:
      case ActivityNameEnum.END_DATE:
        return formatDate(value, DateFormatEnum.DD_MMM_YYYY);

      default:
        return value != null ? String(value) : EMPTY_STRING;
    }
  };

  return [
    {
      id: 'name',
      label: t('activity_detail_table_title_name'),
      render: (record: IActivityDetail) => convertToTitleCase(record?.name),
    },
    {
      id: 'oldValue',
      label: t('activity_detail_table_title_old_value'),
      minWidth: 100,
      render: (record: IActivityDetail) => renderValue(record?.name, record?.oldValue),
    },
    {
      id: 'newValue',
      label: t('activity_detail_table_title_new_value'),
      render: (record: IActivityDetail) => renderValue(record?.name, record?.newValue),
    },
  ];
};

const columnsFile = (t: TFunction): IHeaderCellType<IActivityDetail>[] => {
  const renderValue = (name: string, value: any) => {
    switch (name) {
      case ActivityNameEnum.FILE_URL:
        return (
          <RouterLink to={value} target='_blank'>
            <Link>{value}</Link>
          </RouterLink>
        );

      default:
        return value != null ? String(value) : EMPTY_STRING;
    }
  };

  return [
    {
      id: 'name',
      label: t('activity_detail_table_title_name'),
      render: (record: IActivityDetail) => convertToTitleCase(record?.name),
    },
    {
      id: 'oldValue',
      label: t('activity_detail_table_title_old_value'),
      minWidth: 100,
      render: (record: IActivityDetail) => renderValue(record?.name, record?.oldValue),
    },
    {
      id: 'newValue',
      label: t('activity_detail_table_title_new_value'),
      render: (record: IActivityDetail) => renderValue(record?.name, record?.newValue),
    },
  ];
};

const columnsFolder = (t: TFunction): IHeaderCellType<IActivityDetail>[] => {
  const renderValue = (name: string, value: any) => {
    switch (name) {
      case ActivityNameEnum.IS_CURRENT_APPLY:
        return value != null ? formatTitleCase(String(value)) : EMPTY_STRING;

      default:
        return value != null ? String(value) : EMPTY_STRING;
    }
  };

  return [
    {
      id: 'name',
      label: t('activity_detail_table_title_name'),
      render: (record: IActivityDetail) => convertToTitleCase(record?.name),
    },
    {
      id: 'oldValue',
      label: t('activity_detail_table_title_old_value'),
      minWidth: 100,
      render: (record: IActivityDetail) => renderValue(record?.name, record?.oldValue),
    },
    {
      id: 'newValue',
      label: t('activity_detail_table_title_new_value'),
      render: (record: IActivityDetail) => renderValue(record?.name, record?.newValue),
    },
  ];
};

const columnsEstimate = (t: TFunction): IHeaderCellType<IActivityDetail>[] => {
  const renderValue = (name: string, value: any) => {
    switch (name) {
      case ActivityNameEnum.ASSIGNEE:
        return <BaseAvatarGroup avatars={value || []} borderColor={varColor.white} max={5} />;

      // case ActivityNameEnum.TAGS:
      case ActivityNameEnum.TYPE:
      case ActivityNameEnum.SOURCE:
      case ActivityNameEnum.REFERRAL_TYPE:
      case ActivityNameEnum.UNIT:
        return formatTitleCase(value || EMPTY_STRING);

      case ActivityNameEnum.START_DATE:
      case ActivityNameEnum.END_DATE:
        return formatDate(value, DateFormatEnum.DD_MMM_YYYY);

      case ActivityNameEnum.TOTAL:
        return value != null ? `$${value}` : EMPTY_STRING;

      default:
        return value != null ? String(value) : EMPTY_STRING;
    }
  };

  return [
    {
      id: 'name',
      label: t('activity_detail_table_title_name'),
      render: (record: IActivityDetail) => convertToTitleCase(record?.name),
    },
    {
      id: 'oldValue',
      label: t('activity_detail_table_title_old_value'),
      minWidth: 100,
      render: (record: IActivityDetail) => renderValue(record?.name, record?.oldValue),
    },
    {
      id: 'newValue',
      label: t('activity_detail_table_title_new_value'),
      render: (record: IActivityDetail) => renderValue(record?.name, record?.newValue),
    },
  ];
};

const ActivityDetailTable = (props: Props) => {
  //#region Destructuring Props
  const { data } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/activity', 'common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const renderColumnsTable = () => {
    switch (data?.businessModel) {
      case BusinessModelEnum.NOTE:
        return columnsNote(t);

      case BusinessModelEnum.JOB:
        return columnsJob(t);

      case BusinessModelEnum.FILE:
        return columnsFile(t);

      case BusinessModelEnum.FOLDER:
        return columnsFolder(t);

      case BusinessModelEnum.SCHEDULED:
        return columnsSchedule(t);

      case BusinessModelEnum.ESTIMATE:
        return columnsEstimate(t);

      default:
        return columnsDefault(t);
    }
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id={'activityDetailTable'}>
      <BaseTable columns={renderColumnsTable()} dataSource={data?.detail || []} />
    </Box>
  );
};
export default ActivityDetailTable;
